import type { Accessor } from "solid-js"
import { CatmullRomCurve3, Color, DoubleSide, Group, Mesh, MeshLambertMaterial, Vector2, Vector3 } from "three"
import type { DraftMove } from "../../../core/draft_move/DraftMove.type"
import type { Engine } from "../../../core/engine/Engine.type"
import findByXY from "../../../core/findByXY"
import type { TilePositionXY } from "../../../core/tile_position_xy/TilePositionXY.type"
import coord from "../../../core/tile_position_xy/coord"
import createEntityGridXYV2 from "../../../core/tile_position_xy/createEntityGridXYV2"
import { floor, HALF_PI, max, min } from "../../../core/util/math/Math"
import { deepClone } from "../../../deep_clone"
import { VERTICES_PER_TILE } from "../../consts"
import disposeForTraverse from "../../fn/disposeForTraverse"
import ZoneOfControlGeometry from "./ZoneOfControlGeometry.class"
import createBorderLoopsCollectionV2 from "./createBorderLoopsCollectionV2"
import createAnnexListGrid from "../../../core/tile_position_xy/AnnexListGrid/createAnnexListGrid"
import createCargoListGrid from "../../../core/tile_position_xy/CargoListGrid/createCargoListGrid"
import createDefenseValueGrid from "../../../core/tile_position_xy/DefenseValueGrid/createDefenseValueGrid"
import createEnemyListGrid from "../../../core/tile_position_xy/EnemyListGrid/createEnemyListGrid"
import createImpassValueGridFromMovementCostGrid from "../../../core/tile_position_xy/ImpassValueGrid/createImpassValueGridFromMovementCostGrid"
import createMergableValueGrid from "../../../core/tile_position_xy/MergableValueGrid/createMergableValueGrid"
import createMovementCostGridUInt8Array from "../../../core/tile_position_xy/MovementCostGrid/createMovementCostGridUInt8Array"
import createOccupantValueGrid from "../../../core/tile_position_xy/OccupantValueGrid/createOccupantValueGrid"
import createTaxiListGrid from "../../../core/tile_position_xy/TaxiListGrid/createTaxiListGrid"
import createFindMostEfficientPathToTileGetter from "../../../core/tile_position_xy/createFindMostEfficientPathToTileGetter/createFindMostEfficientPathToTileGetter"

const grey = new Color(0x777777)

const GET_ZONE_VERSION : 1 | 2 = 2

export default class SingleColorZoneOfControlGroup extends Group {

  name = 'ZoneOfControlGroup'

  engine: Engine
  units: Array<DraftMove['unit']> = []

  color: number

  constructor(
    engine: Engine,
    color: number,
  ) {
    super()
    this.engine = engine
    this.color = color
  }

  ents() {
    return deepClone(this.engine.state.ents)
  }

  // maximum number of turns to consider (aka zones)
  max(units: Array<DraftMove['unit']>) {
    const { width, height } = this.engine.state

    const avgGridSize = (width + height) / 2

    const minUnitMobility = units.reduce((carry: number, unit) => min(carry, unit.mobility ?? 0), 16)
    const mobility = max(2, minUnitMobility)
    const value = floor(avgGridSize / mobility)

    console.log('SingleColorZoneOfControlGroup.max', { avgGridSize, mobility, value: avgGridSize / mobility })

    return value
  }

  async update(
    units: Array<DraftMove['unit']>,
  ): Promise<void> {
    console.log('SingleColorZoneOfControlGroup.update()', deepClone(units))
    console.time('SingleColorZoneOfControlGroup.update')
    // console.profile('SingleColorZoneOfControlGroup.update')
    this.units = units = deepClone(units)
    const { engine } = this
    const { state } = engine
    const { width: gridWidth, height: gridHeight } = state

    const maxZones: number = this.max(units)

    // console.time('SingleColorZoneOfControlGroup.update.createEntityGridXY')
    // const entityGridXY = createEntityGridXY(this.ents(), gridWidth, gridHeight)
    const entityGridXY = createEntityGridXYV2(this.ents(), gridWidth, gridHeight)
    // console.timeEnd('SingleColorZoneOfControlGroup.update.createEntityGridXY')

    // console.time('SingleColorZoneOfControlGroup.update.createLazyEfficientPathGrid')
    const unitToEfficientPathGridsList = units.map((unit) => {
      // return createLazyEfficientPathGridV3(gridWidth, gridHeight, entityGridXY, unit)

      const grid = entityGridXY
      const movementCostGrid = createMovementCostGridUInt8Array(gridWidth, gridHeight, grid, unit)
      const impassValueGrid = createImpassValueGridFromMovementCostGrid(movementCostGrid)
      const enemyListGrid = createEnemyListGrid(gridWidth, gridHeight, grid, unit)
      const annexListGrid = createAnnexListGrid(gridWidth, gridHeight, grid, unit)
      const occupantValueGrid = createOccupantValueGrid(gridWidth, gridHeight, grid, unit)
      const defenseValueGrid = createDefenseValueGrid(gridWidth, gridHeight, grid, unit)
      const mergableValueGrid = createMergableValueGrid(gridWidth, gridHeight, grid, unit)
      const taxiListGrid = createTaxiListGrid(gridWidth, gridHeight, grid, unit)
      const cargoListGrid = createCargoListGrid(gridWidth, gridHeight, grid, unit)

      return createFindMostEfficientPathToTileGetter(
        grid, unit,
        gridWidth, gridHeight,
        movementCostGrid,
        enemyListGrid,
        annexListGrid,
        occupantValueGrid,
        defenseValueGrid,
        impassValueGrid,
        mergableValueGrid,
        taxiListGrid,
        cargoListGrid
      )
    })
    // console.timeEnd('SingleColorZoneOfControlGroup.update.createLazyEfficientPathGrid')

    function getZoneV1(x: number, y: number): number {
      if (units.find(unit => unit.x === x && unit.y === y)) {
        return 1
      }
      return max(0, ...unitToEfficientPathGridsList.map((epGrid) => {
        const eptt1 = epGrid(x, y)
        const zone1 = eptt1?.turns.length || 0

        if (zone1 >= 0) {
          return zone1
        }
        return 99
      }))
    }

    function getZoneV2(x: number, y: number): number {
      if (findByXY(units, x, y)) {
        return 1
      }
      let maxZone = 0
      for (let i = 0; i < unitToEfficientPathGridsList.length; i++) {
        const epGrid = unitToEfficientPathGridsList[i]
        const eptt1 = epGrid(x, y)
        const zone1 = eptt1?.turns.length || 0
        if (zone1 >= 0) {
          maxZone = max(maxZone, zone1)
        } else {
          maxZone = 99
        }
      }
      return maxZone
    }
    const getZone = GET_ZONE_VERSION === 1 ? getZoneV1 : getZoneV2

    const originUnit: TilePositionXY = units[0] ?? coord(0, 0)
    const originX = originUnit.x
    const originY = originUnit.y
    const maxManhattanDistance = max(gridWidth, gridHeight)
    let zone: number

    function insideBorder(x: number, y: number): boolean {
      // return true
      const z0 = getZone(x, y)
      if (z0 === 0) {
        return false
      }
      return z0 <= zone
    }

    const getZoneColor = (zone: number): Color => {
      if (zone === 2) {
        return new Color(this.color).lerp(grey, 0.5)
      }
      if (zone === 1) {
        return new Color(this.color)
      }
      return new Color(grey)
    }

    // console.time('SingleColorZoneOfControlGroup.update.meshSetup')
    const zoneMeshList: Array<Accessor<Mesh>> = []
    const material = new MeshLambertMaterial({
      transparent: true,
      side: DoubleSide,
      vertexColors: true,
      depthTest: false,
    })
    // console.timeEnd('SingleColorZoneOfControlGroup.update.meshSetup')

    for (zone = 1; zone <= maxZones; zone++) {
      const zoneColor = getZoneColor(zone)

      console.time('SingleColorZoneOfControlGroup.update.createBorderLoopsCollectionV2')
      const loops = await createBorderLoopsCollectionV2(
        originX, originY, gridWidth, gridHeight, maxManhattanDistance, insideBorder)
      console.timeEnd('SingleColorZoneOfControlGroup.update.createBorderLoopsCollectionV2')

      // console.time('SingleColorZoneOfControlGroup.update.loopsProcessing')
      loops.forEach((points) => {

        const firstPoint = points[0]
        const lastPoint = points.length > 1 ? points.at(-1) : null
        if (lastPoint && firstPoint.v2.equals(lastPoint.v2)) {
          points.pop()
        }
        const rawPointsV3 = points.map((p): Vector3 => {
          const v2: Vector2 = p.v2
          const v3 = new Vector3(v2.x - 0.5, 0, v2.y - 0.5)
          // v3.multiplyScalar(VERTICES_PER_TILE)
          return v3
        })

        function toEdgePoints(carry: Array<Vector3>, currentV3: Vector3, currentIndex: number, inputList: Array<Vector3>): Array<Vector3> {
          const nextV3 = inputList[currentIndex + 1] || inputList[0]
          const edge25 = currentV3.clone().lerp(nextV3.clone(), 0.2)
          const edge5 = currentV3.clone().lerp(nextV3.clone(), 0.5)
          const edge75 = currentV3.clone().lerp(nextV3.clone(), 0.8)
          carry.push(edge25, edge5, edge75)
          return carry
        }
        const pointsV3 = rawPointsV3.reduce(toEdgePoints, [])
        // move the first to the end,
        // so that the start and end share an edge instead,
        // which probably looks better
        pointsV3.push(pointsV3.shift()!)

        const curve = new CatmullRomCurve3(pointsV3, true)
        const numCurveSegments = ((pointsV3.length - 1) * 2)

        const splines: Array<{ value: number, color(): Color, points: Array<Vector3> }> = [
          { value: 0, color: () => zoneColor.clone(), points: [] },
          { value: -0.3, color: () => zoneColor.clone(), points: [] },
          { value: -2.5, color: () => zoneColor.clone(), points: [] },
        ]
        splines.forEach(sg => sg.value *= 0.1)
        const frames = curve.computeFrenetFrames(numCurveSegments, curve.closed)

        curve.getSpacedPoints(numCurveSegments).forEach((point0, index) => {
          const normal = frames.binormals[index]
          splines.forEach((sg) => {
            const point1 = point0.clone().addScaledVector(normal, -sg.value)
            sg.points.push(point1)
          })
        })

        zoneMeshList.push(() => {
          const segments = 2
          const geometry = new ZoneOfControlGeometry(
            splines.map(sg => sg.points.map(v3 => new Vector2(v3.x, v3.z))),
            VERTICES_PER_TILE,
            segments, splines.map(sg => sg.color().getHex()))

          geometry.rotateX(HALF_PI)

          const mesh = new Mesh(geometry, material)
          return mesh
        })
      })
      // console.timeEnd('SingleColorZoneOfControlGroup.update.loopsProcessing')
    }
    let extraY = 1
    zoneMeshList
      .reverse()
      .forEach((getMesh) => {
        const mesh0 = getMesh()
        mesh0.position.y += extraY
        extraY += 0.05
        this.add(mesh0)
      })
    console.timeEnd('SingleColorZoneOfControlGroup.update')
    // console.profileEnd('SingleColorZoneOfControlGroup.update')
  }

  reset() {
    // console.log('SingleColorZoneOfControlGroup.reset')
    this.units = []
    this.traverse(disposeForTraverse)
    this.clear()
  }
}
